import { Formik } from 'formik';
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { useGetActiveAcTypesQuery, useGetActiveAircraftsQuery } from 'features/apiSlice';
import { rotationSchema } from './constants';


const RotationFormDialog = ({
  open,
  onClose,
  data,
  initialValues,
  seasons,
  onSave,
}) => {
  const { data: acTypesData, isLoading: loadingAcTypes } = useGetActiveAcTypesQuery(undefined, { skip: !open });
  const { data: aircraftsData, isLoading: loadingAircrafts } = useGetActiveAircraftsQuery(undefined, { skip: !open });

  const acTypes   = acTypesData   || [];
  const aircrafts = aircraftsData || [];

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>{data ? 'Edit Rotation' : 'New Rotation'}</DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={rotationSchema}
        onSubmit={onSave}
        enableReinitialize
      >
        {({ 
          values, 
          errors, 
          touched, 
          handleBlur, 
          handleSubmit, 
          isSubmitting, 
          setFieldValue 
        }) => {
          const upper = (name) => (e) => setFieldValue(name, e.target.value.toUpperCase());

          const filteredAircrafts = values.acType
            ? aircrafts.filter((a) => a.acTypeCode === values.acType)
            : aircrafts;

          return (
            <form onSubmit={handleSubmit}>
              <DialogContent sx={{ pt: 1.5, pb: 1 }}>
                {/* Season */}
                <TextField
                  select 
                  fullWidth 
                  size="small" 
                  label="Season *" 
                  name="seasonId"
                  value={values.seasonId}
                  onChange={(e) => setFieldValue('seasonId', e.target.value)}
                  error={touched.seasonId && Boolean(errors.seasonId)}
                  helperText={touched.seasonId && errors.seasonId}
                  sx={{ mb: 2 }}
                >
                  {seasons.map((s) => (
                    <MenuItem key={s._id} value={s._id}>{s.seasonCode} - {s.seasonName}</MenuItem>
                  ))}
                </TextField>

                <Box display="flex" gap={1} alignItems="flex-start" mb={1.5}>
                  {/* AC Type — select from active acTypes */}
                  <TextField
                    select 
                    fullWidth 
                    size="small" 
                    label="AC Type *" 
                    name="acType"
                    value={values.acType}
                    onChange={(e) => {
                      setFieldValue('acType', e.target.value);
                      setFieldValue('acReg', '');
                    }}
                    onBlur={handleBlur}
                    error={touched.acType && Boolean(errors.acType)}
                    helperText={touched.acType && errors.acType}
                    disabled={loadingAcTypes}
                    sx={{ flex: 1, '& input': { fontFamily: 'monospace' } }}
                  >
                    {loadingAcTypes
                      ? <MenuItem disabled><CircularProgress size={14} /></MenuItem>
                      : acTypes.map((t) => (
                          <MenuItem key={t._id} value={t.acTypeCode}>
                            {t.acTypeCode}{t.description ? ` — ${t.description}` : ''}
                          </MenuItem>
                        ))
                    }
                  </TextField>

                  {/* AC Reg — select filtered by acType */}
                  <TextField
                    select 
                    fullWidth 
                    size="small" 
                    label="AC Reg" 
                    name="acReg"
                    value={values.acReg}
                    onChange={(e) => setFieldValue('acReg', e.target.value)}
                    disabled={loadingAircrafts || !values.acType}
                    sx={{ flex: 1, '& input': { fontFamily: 'monospace' } }}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {loadingAircrafts
                      ? <MenuItem disabled><CircularProgress size={14} /></MenuItem>
                      : filteredAircrafts.map((a) => (
                          <MenuItem key={a._id} value={a.acReg}>{a.acReg}</MenuItem>
                        ))
                    }
                  </TextField>
                </Box>

                {/* Rotation Code */}
                <TextField
                  fullWidth size="small" label="Rotation Code *" name="rotationCode"
                  value={values.rotationCode}
                  onChange={upper('rotationCode')}
                  onBlur={handleBlur}
                  error={touched.rotationCode && Boolean(errors.rotationCode)}
                  helperText={touched.rotationCode && errors.rotationCode}
                  placeholder="ROT-A320-001"
                  autoFocus={!data}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  sx={{ mb: 1.5, '& input': { fontFamily: 'monospace', fontWeight: 600 } }}
                />

                <TextField
                  fullWidth size="small" multiline rows={2} label="Notes" name="notes"
                  value={values.notes}
                  onChange={(e) => setFieldValue('notes', e.target.value)}
                />
              </DialogContent>

              <DialogActions>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="outlined" 
                  disabled={isSubmitting}
                >
                  {data ? 'Update' : 'Create'}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default RotationFormDialog;
